/**
 * Single-slot hover-intent for contextual column insertion.
 * Only one insert index is armed at a time; one shared timer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { COLUMN_INSERT_HOVER_DELAY_MS } from './kanban.utils';

export function useKanbanColumnInsertIntent(enabled: boolean) {
  const [armedInsertSlot, setArmedInsertSlot] = useState<number | null>(null);
  const pendingSlotRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const disarmAll = useCallback(() => {
    clearTimer();
    pendingSlotRef.current = null;
    setArmedInsertSlot(null);
  }, [clearTimer]);

  useEffect(() => {
    if (!enabled) disarmAll();
  }, [enabled, disarmAll]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  const onPointerIntentStart = useCallback(
    (slot: number) => {
      if (!enabled) return;
      clearTimer();
      pendingSlotRef.current = slot;
      timerRef.current = setTimeout(() => {
        if (pendingSlotRef.current === slot) {
          setArmedInsertSlot(slot);
        }
        timerRef.current = null;
      }, COLUMN_INSERT_HOVER_DELAY_MS);
    },
    [clearTimer, enabled]
  );

  const onPointerIntentEnd = useCallback(
    (slot: number) => {
      if (pendingSlotRef.current === slot) {
        clearTimer();
        pendingSlotRef.current = null;
      }
      setArmedInsertSlot((current) => (current === slot ? null : current));
    },
    [clearTimer]
  );

  return {
    armedInsertSlot,
    onPointerIntentStart,
    onPointerIntentEnd,
    disarmAll,
  };
}
